import { Link } from "react-router-dom";
import { ArrowUpRight, Circle, Radio } from "lucide-react";
import Hero from "../components/Hero";
import Marquee from "../components/Marquee";
import { Reveal } from "../components/Reveal";
import AnimatedStat from "../components/AnimatedStat";
import { FILMS, CONCEPTS, STATUSES } from "../data/films";

export default function Home() {
  const completed = FILMS.filter((f) => f.status === STATUSES.COMPLETED);
  const developed = FILMS.filter((f) => f.status === STATUSES.DEVELOPED);
  const inProd = FILMS.filter((f) => f.status === STATUSES.IN_PRODUCTION);
  const onFloor = FILMS.filter((f) => f.status === STATUSES.ON_THE_FLOOR);

  return (
    <main data-testid="home-page" className="bg-black">
      <Hero />

      {/* About */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-28 md:py-36 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
        <Reveal className="md:col-span-4">
          <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)]">I. About</p>
        </Reveal>
        <Reveal delay={1} className="md:col-span-8">
          <h2 className="font-serif-display font-light text-4xl md:text-6xl lg:text-7xl leading-[1.02] text-white tracking-tight">
            A creative film company dedicated to{" "}
            <span className="italic text-[color:var(--dk-gold)]">culturally meaningful</span> stories.
          </h2>
          <p className="mt-10 max-w-2xl text-white/70 leading-relaxed text-lg">
            Founded in 2019 and based in New Baneshwor, Kathmandu, Dhara Kala Movies
            produces documentaries and cultural films for private events, festivals and
            informative programming — with a special focus on social awareness. In
            collaboration with leading Nepali production houses, we preserve powerful
            stories and present them through modern, internationally engaging cinema.
          </p>

          {/* Animated Stats */}
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 md:gap-10">
            <AnimatedStat index={0} value={15} suffix="+" label="Film Projects" />
            <AnimatedStat index={1} value={40} suffix="+" label="Creative Professionals" />
            <AnimatedStat index={2} value={6} suffix="+" label="Completed Works" />
            <AnimatedStat index={3} value="10K" suffix="+" label="Community Viewers" />
            <AnimatedStat index={4} value={20} suffix="+" label="Screening Venues" />
            <AnimatedStat index={5} value="Intl." label="Festival Vision" />
          </div>
        </Reveal>
      </section>

      <Marquee
        items={[
          "Historical & Spiritual",
          "Kathmandu · Dharan · Halesi · Mustang",
          "Cinema of the Sacred",
          "Nepali Voices · Global Screens",
        ]}
        testid="home-marquee"
      />

      {/* Completed Works — filmography wall (horizontal scroll) */}
      <section className="py-28 md:py-36 overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 items-end gap-8 mb-14">
            <Reveal className="md:col-span-8">
              <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-6">
                II. Completed Works · Filmography
              </p>
              <h2 className="font-serif-display font-light text-5xl md:text-7xl leading-[0.95] text-white tracking-tighter">
                Six released,<br />
                <span className="italic text-[color:var(--dk-gold)]">already carrying the studio.</span>
              </h2>
              <p className="mt-6 text-white/60 max-w-2xl leading-relaxed">
                Short films and documentaries screened across festivals in Nepal and South Asia — the foundation on which the feature slate now builds.
              </p>
            </Reveal>
            <Reveal delay={1} className="md:col-span-4 md:text-right">
              <span className="text-[10px] uppercase tracking-overline text-white/50">
                ← Scroll →
              </span>
            </Reveal>
          </div>
        </div>

        {/* Horizontal scroll wall */}
        <div
          data-testid="filmography-wall"
          className="flex gap-4 md:gap-6 overflow-x-auto snap-x snap-mandatory pb-8 pl-6 md:pl-10 pr-6 md:pr-10 [scrollbar-width:thin] [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-thumb]:bg-[color:var(--dk-gold)] [&::-webkit-scrollbar-track]:bg-white/5"
        >
          {completed.map((f, i) => (
            <Reveal
              key={f.slug}
              delay={i % 3}
              className="group relative shrink-0 w-[78vw] sm:w-[46vw] md:w-[34vw] lg:w-[26vw] xl:w-[22vw] snap-start overflow-hidden border border-white/10 bg-[color:var(--dk-muted)] hover:border-[color:var(--dk-gold)]/60 transition-colors"
              data-testid={`home-completed-${f.slug}`}
            >
              <Link to={`/films/${f.slug}`} className="absolute inset-0 z-10">
                <span className="sr-only">{f.title}</span>
              </Link>
              <div className="relative aspect-[3/4] overflow-hidden">
                <img src={f.poster} alt={f.title} className="film-hover-img absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <span className="absolute top-4 left-4 text-[10px] uppercase tracking-overline text-black bg-[color:var(--dk-gold)] px-3 py-1">
                  {f.year} · {f.runtime}
                </span>
                <ArrowUpRight size={18} className="absolute top-4 right-4 text-white/70 group-hover:text-[color:var(--dk-gold)] transition-colors" />
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)]/80 mb-2">{f.genre}</p>
                  <h3 className="font-serif-display text-white text-xl md:text-2xl leading-tight">{f.title}</h3>
                  <p className="mt-1 text-white/50 text-[11px] font-serif-display italic">{f.nepaliTitle}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Developed Projects */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-28 md:py-36">
        <div className="grid grid-cols-1 md:grid-cols-12 items-end gap-8 mb-14">
          <Reveal className="md:col-span-8">
            <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-6">III. Developed Projects</p>
            <h2 className="font-serif-display font-light text-5xl md:text-7xl lg:text-8xl leading-[0.95] text-white tracking-tighter">
              Seven films.<br />
              <span className="italic">One vision.</span>
            </h2>
          </Reveal>
          <Reveal delay={1} className="md:col-span-4 md:text-right">
            <Link data-testid="home-view-all-projects" to="/films" className="text-[11px] uppercase tracking-overline text-white eyebrow-link">
              All Projects →
            </Link>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-6">
          {developed.map((film, i) => (
            <PosterCard
              key={film.slug}
              film={film}
              index={i}
              className={
                i === 0 ? "md:col-span-6 md:row-span-2 md:h-[640px]" :
                i < 3 ? "md:col-span-3 md:h-[310px]" :
                i < 5 ? "md:col-span-4 md:h-[380px]" :
                "md:col-span-4 md:h-[380px]"
              }
              big={i === 0}
            />
          ))}
        </div>
      </section>

      {/* In Production preview */}
      <section className="border-y border-white/10 bg-[color:var(--dk-muted)]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-28 md:py-36">
          <div className="grid grid-cols-1 md:grid-cols-12 items-end gap-8 mb-14">
            <Reveal className="md:col-span-8">
              <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-6 flex items-center gap-3">
                <Circle size={8} className="fill-[color:var(--dk-red)] text-[color:var(--dk-red)] animate-pulse" />
                IV. In Production
              </p>
              <h2 className="font-serif-display font-light text-5xl md:text-7xl leading-[0.95] text-white tracking-tighter">
                Cameras <span className="italic text-[color:var(--dk-gold)]">rolling.</span>
              </h2>
            </Reveal>
            <Reveal delay={1} className="md:col-span-4 md:text-right">
              <Link data-testid="home-view-in-production" to="/in-production" className="text-[11px] uppercase tracking-overline text-white eyebrow-link">
                See In-Production Slate →
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {inProd.map((f, i) => (
              <ProductionCard key={f.slug} film={f} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* On the Floor preview */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-28 md:py-36">
        <div className="grid grid-cols-1 md:grid-cols-12 items-end gap-8 mb-14">
          <Reveal className="md:col-span-8">
            <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-red)] mb-6 flex items-center gap-3">
              <Radio size={12} className="animate-pulse" />
              V. Now Filming
            </p>
            <h2 className="font-serif-display font-light text-5xl md:text-7xl leading-[0.95] text-white tracking-tighter">
              On the <span className="italic text-[color:var(--dk-gold)]">floor.</span>
            </h2>
          </Reveal>
          <Reveal delay={1} className="md:col-span-4 md:text-right">
            <Link data-testid="home-view-on-the-floor" to="/on-the-floor" className="text-[11px] uppercase tracking-overline text-white eyebrow-link">
              Enter the Set →
            </Link>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {onFloor.map((f, i) => (
            <FloorCard key={f.slug} film={f} index={i} />
          ))}
        </div>
      </section>

      {/* Future concepts */}
      <section className="border-y border-white/10 bg-[color:var(--dk-muted)]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-28 md:py-36">
          <Reveal className="mb-14">
            <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-6">VI. Stories We Are Developing Next</p>
            <h2 className="font-serif-display font-light text-5xl md:text-7xl leading-[0.95] text-white tracking-tighter max-w-4xl">
              The next chapter, <span className="italic">already writing itself.</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {CONCEPTS.map((c, i) => (
              <Reveal key={c.slug} delay={i}
                className="group border border-white/10 bg-black p-8 md:p-10 hover:border-[color:var(--dk-gold)]/50 transition-colors"
                data-testid={`concept-${c.slug}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <span className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] border border-[color:var(--dk-gold)]/50 px-3 py-1">
                    {c.status}
                  </span>
                  <span className="text-[10px] uppercase tracking-overline text-white/50">{c.genre}</span>
                </div>
                <h3 className="font-serif-display text-white text-3xl md:text-4xl leading-tight tracking-tight">{c.title}</h3>
                <p className="mt-2 text-white/50 text-sm font-serif-display italic">
                  {c.nepaliTitle} · {c.hindiTitle}
                </p>
                <p className="mt-6 text-white/70 leading-relaxed">{c.shortSynopsis}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-28 md:py-36">
        <Reveal>
          <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-8">VII. Correspondence</p>
          <h2 className="font-serif-display font-light text-6xl md:text-8xl lg:text-9xl leading-[0.9] text-white tracking-tighter">
            Let&apos;s make <span className="italic text-[color:var(--dk-gold)]">something</span><br /> that lingers.
          </h2>
          <Link data-testid="home-cta-contact" to="/contact"
            className="mt-12 inline-flex items-center gap-3 text-white text-lg group">
            <span className="border-b border-white/40 pb-1">Begin a conversation</span>
            <ArrowUpRight size={20} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </Reveal>
      </section>
    </main>
  );
}

function PosterCard({ film, index, className = "", big }) {
  return (
    <Reveal delay={index} className={`relative group overflow-hidden bg-[color:var(--dk-muted)] ${className}`}>
      <Link to={`/films/${film.slug}`} data-testid={`home-film-${film.slug}`} className="absolute inset-0 z-10">
        <span className="sr-only">{film.title}</span>
      </Link>
      <img src={film.poster} alt={film.title} className="film-hover-img absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-black/10" />
      <div className="relative z-[5] h-full min-h-[300px] flex flex-col justify-between p-6 md:p-8">
        <div className="flex items-start justify-between">
          <span className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)]">
            {String(index + 1).padStart(2, "0")} · {film.status}
          </span>
          <ArrowUpRight size={18} className="text-white/60 group-hover:text-[color:var(--dk-gold)] transition-colors" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-overline text-white/50 mb-2">{film.genre}</p>
          <h3 className={`font-serif-display font-light text-white leading-[0.98] tracking-tight ${big ? "text-4xl md:text-6xl" : "text-2xl md:text-3xl"}`}>
            {film.title}
          </h3>
        </div>
      </div>
    </Reveal>
  );
}

function ProductionCard({ film, index }) {
  return (
    <Reveal delay={index} className="group relative bg-black border border-white/10 overflow-hidden hover:border-[color:var(--dk-red)]/60 transition-colors">
      <Link to={`/films/${film.slug}`} data-testid={`home-in-prod-${film.slug}`} className="absolute inset-0 z-10">
        <span className="sr-only">{film.title}</span>
      </Link>
      <div className="relative h-64 overflow-hidden">
        <img src={film.poster} alt={film.title} className="film-hover-img absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/90" />
        <span className="absolute top-4 left-4 flex items-center gap-2 text-[10px] uppercase tracking-overline text-white bg-[color:var(--dk-red)] px-3 py-1">
          <Circle size={6} className="fill-white animate-pulse" /> In Production
        </span>
      </div>
      <div className="p-6">
        <h3 className="font-serif-display text-white text-2xl leading-tight">{film.title}</h3>
        <p className="mt-1 text-white/50 text-xs font-serif-display italic">{film.nepaliTitle}</p>
        <p className="mt-3 text-white/60 text-sm leading-relaxed line-clamp-3">{film.shortSynopsis}</p>

        <div className="mt-6">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-overline text-white/60 mb-2">
            <span>Progress</span>
            <span className="text-[color:var(--dk-gold)]">{film.progress}%</span>
          </div>
          <div className="h-px bg-white/10 relative">
            <div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-[color:var(--dk-red)] to-[color:var(--dk-gold)]"
              style={{ width: `${film.progress}%`, height: "2px", top: "-1px" }}
            />
          </div>
        </div>
      </div>
    </Reveal>
  );
}

function FloorCard({ film, index }) {
  return (
    <Reveal delay={index} className="group relative overflow-hidden border border-white/10 bg-black hover:border-[color:var(--dk-gold)]/50 transition-colors">
      <Link to={`/films/${film.slug}`} data-testid={`home-floor-${film.slug}`} className="absolute inset-0 z-10">
        <span className="sr-only">{film.title}</span>
      </Link>
      <div className="grid grid-cols-2">
        <div className="relative h-80 overflow-hidden">
          <img src={film.poster} alt={film.title} className="film-hover-img absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/80" />
        </div>
        <div className="p-6 md:p-8 flex flex-col justify-between">
          <div>
            <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-overline text-[color:var(--dk-red)] mb-4">
              <Radio size={12} className="animate-pulse" /> Now Filming
            </span>
            <h3 className="font-serif-display text-white text-2xl md:text-3xl leading-tight">{film.title}</h3>
            <p className="mt-1 text-white/50 text-xs font-serif-display italic">{film.nepaliTitle}</p>
          </div>
          <div className="text-[10px] uppercase tracking-overline text-white/60">
            <p><span className="text-[color:var(--dk-gold)]/70 mr-2">Location</span>{film.location}</p>
            <p className="mt-2"><span className="text-[color:var(--dk-gold)]/70 mr-2">Genre</span>{film.genre}</p>
          </div>
        </div>
      </div>
    </Reveal>
  );
}
